import RiAuctionLine from '@ben/icons/auction-line';
import RiCustomerServiceLine from '@ben/icons/customer-service-line';
import RiExternalLinkLine from '@ben/icons/external-link-line';
import RiFileListLine from '@ben/icons/file-list-line';
import RiListCheck3Line from '@ben/icons/list-check-3';
import RiMap2Line from '@ben/icons/map-2-line';
import RiQuestionLine from '@ben/icons/question-line';
import RiSmartphoneLine from '@ben/icons/smartphone-line';
import MantineTooltip from '@ben/orion/mantine-tooltip';
import type { MenuItemProps } from '@ben/orion/menu';
import Menu from '@ben/orion/menu';
import RIcon from '@ben/orion/remix-icon';
import { show } from '@intercom/messenger-js-sdk';
import { useDisclosure } from '@mantine/hooks';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { LeftNavigationActionIcon } from '../common/action-icon';

export interface HelpMenuProps {
  faqUrl?: string;
  mobileAppUrl?: string;
  termsAndConditionsUrl?: string;
  hasIntercom: boolean;
  onStartWelcomeJourney?: () => void;
  onStartOnboardingChecklist?: () => void;
}

interface MenuItem extends MenuItemProps {
  show: boolean;
  key: string;
}

const HelpMenu = ({
  faqUrl,
  mobileAppUrl,
  termsAndConditionsUrl,
  hasIntercom,
  onStartWelcomeJourney,
  onStartOnboardingChecklist,
}: HelpMenuProps) => {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure();

  if (
    !hasIntercom &&
    !faqUrl &&
    !termsAndConditionsUrl &&
    !mobileAppUrl &&
    !onStartOnboardingChecklist &&
    !onStartWelcomeJourney
  ) {
    return;
  }

  const onChat = () => {
    // show intercom chat
    show();
  };

  const menuItems: MenuItem[] = [
    {
      key: 'intercom',
      children: t('leftNavigation.contactSupport'),
      leftSection: <RIcon icon={RiCustomerServiceLine} />,
      onClick: onChat,
      show: hasIntercom,
    },
    {
      key: 'faq',
      children: t('leftNavigation.frequentlyAskedQuestions'),
      leftSection: <RIcon icon={RiFileListLine} />,
      href: faqUrl,
      component: 'a',
      target: '_blank',
      rel: 'noopener noreferrer',
      show: !!faqUrl,
    },
    {
      key: 'terms',
      children: t('leftNavigation.termsAndPolicies'),
      leftSection: <RIcon icon={RiAuctionLine} />,
      href: termsAndConditionsUrl,
      component: 'a',
      target: '_blank',
      rel: 'noopener noreferrer',
      show: !!termsAndConditionsUrl,
    },
    {
      key: 'mobileApp',
      children: t('leftNavigation.downloadBenApp'),
      leftSection: <RIcon icon={RiSmartphoneLine} />,
      href: mobileAppUrl,
      component: 'a',
      target: '_blank',
      rel: 'noopener noreferrer',
      show: !!mobileAppUrl,
    },
    {
      key: 'welcomeJourney',
      children: t('leftNavigation.welcomeGuide'),
      leftSection: <RIcon icon={RiMap2Line} />,
      rightSection: undefined,
      onClick: onStartWelcomeJourney,
      show: !!onStartWelcomeJourney,
    },
    {
      key: 'onboardingChecklist',
      children: t('onboardingChecklist.helpMenu'),
      leftSection: <RIcon icon={RiListCheck3Line} />,
      rightSection: undefined,
      onClick: onStartOnboardingChecklist,
      show: !!onStartOnboardingChecklist,
    },
  ].filter(({ show }) => show);

  return (
    <Menu
      position="bottom-start"
      opened={opened}
      onChange={toggle}
      width="282px"
    >
      <MantineTooltip
        label={t('leftNavigation.support')}
        disabled={opened}
        position="bottom-start"
      >
        <Menu.Target data-testid="help-target">
          <LeftNavigationActionIcon
            aria-label={t('leftNavigation.support')}
            $menuOpened={opened}
          >
            <RIcon icon={RiQuestionLine} size="md" />
          </LeftNavigationActionIcon>
        </Menu.Target>
      </MantineTooltip>
      <Menu.Dropdown>
        {menuItems.map((menuItem, index) => {
          const { show, key, ...menuItemProps } = menuItem;
          const isLast = index === menuItems.length - 1;
          return (
            <Fragment key={key}>
              <Menu.Item
                rightSection={<RIcon icon={RiExternalLinkLine} />}
                {...menuItemProps}
              />
              {!isLast && <Menu.Divider />}
            </Fragment>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};

export default HelpMenu;
